import { createSmartAccountClient as permissionlessCreateSmartAccountClient, } from 'permissionless';
import { http } from 'viem';
import { getBlock } from 'viem/actions';
import { getUserOperationReceipt, isEthGetUserOperationReceiptResponse, } from '../../getUserOperationReceipt';
import { userOperationEventAbi } from '../../userOperationEvent';
import { ERC4337 } from '../../../constants';
import { prepareUserOperation } from '../prepareUserOperation';
import { quoteGasWithUSDC } from '../middleware/paymasters/quoteGasWithUSDC';
export async function createSmartAccount(args) {
    const { owner, bundlerUrl, paymasterProxyUrl, paymasterProxyAuthSecret, spaceDapp, entrypointAddress, publicRpcClient, factoryAddress, smartAccountImpl, fetchAccessTokenFn, usePimlicoPaymaster, enableUsdc, } = args;
    const permissionlessSmartAccountClient = permissionlessCreateSmartAccountClient({
        account: smartAccountImpl,
        client: publicRpcClient,
        bundlerTransport: http(bundlerUrl),
    });
    const wrappedPrepareUserOperation = prepareUserOperation({
        bundlerUrl,
        paymasterProxyUrl,
        paymasterProxyAuthSecret,
        usePimlicoPaymaster,
        fetchAccessTokenFn,
        rootKeyAddress: owner.address,
        enableUsdc: enableUsdc ?? false,
    });
    let waitTimeoutMs = 30_000;
    let waitIntervalMs = 500;
    async function poll(args) {
        const { action, timeoutMs = waitTimeoutMs, intervalMs = waitIntervalMs } = args;
        const start = Date.now();
        const end = Date.now() + timeoutMs;
        while (Date.now() < end) {
            const result = await action();
            if (result) {
                return { duration: Date.now() - start, result };
            }
            await new Promise((resolve) => setTimeout(resolve, intervalMs));
        }
        return { duration: Date.now() - start, result: null };
    }
    return {
        address: await smartAccountImpl.getAddress(),
        client: permissionlessSmartAccountClient,
        type: factoryAddress === ERC4337.SimpleAccount.Factory ? 'simple' : 'modular',
        factoryAddress,
        entrypointAddress,
        publicRpcClient: publicRpcClient,
        parseAllContractErrors: (args) => {
            const { spaceId, error } = args;
            return spaceDapp?.parseAllContractErrors({
                spaceId,
                error,
            });
        },
        quoteGasWithUSDC: async (args) => {
            const { rpcRequest, skipQuoteApi = false } = args;
            return quoteGasWithUSDC({
                smartAccountClient: permissionlessSmartAccountClient,
                rpcRequest,
                bundlerUrl,
                accessToken: (await fetchAccessTokenFn?.()) ?? undefined,
                paymasterProxyAuthSecret,
                paymasterProxyUrl,
                enableUsdc: enableUsdc ?? false,
                skipQuoteApi,
            });
        },
        prepareUserOperation: async (parameters) => wrappedPrepareUserOperation(permissionlessSmartAccountClient, parameters),
        /**
         *
         * @param args { userOpHash: Hex }
         * @returns @link SendUserOperationReturnType
         */
        getUserOperationResponse: async (args) => {
            const { userOpHash } = args;
            const response = {
                userOpHash,
                /**
                 * @deprecated use the "getUserOperationReceipt" function on the user operation response instead
                 * this is old api from userop.js, which we no longer use
                 */
                wait: async () => {
                    const block = await getBlock(publicRpcClient);
                    const polledAction = await poll({
                        action: async () => {
                            const logs = await publicRpcClient.getLogs({
                                address: entrypointAddress,
                                event: userOperationEventAbi,
                                args: {
                                    userOpHash,
                                },
                                fromBlock: block.number > 100n ? block.number - 100n : 0n,
                                toBlock: 'latest',
                            });
                            return logs.length > 0 ? logs[0] : null;
                        },
                    });
                    return polledAction?.result ?? null;
                },
                getUserOperationReceipt: async () => {
                    const polledAction = await poll({
                        action: async () => {
                            try {
                                const receipt = await getUserOperationReceipt({
                                    bundlerClient: permissionlessSmartAccountClient,
                                    userOpHash,
                                });
                                if (isEthGetUserOperationReceiptResponse(receipt)) {
                                    return receipt;
                                }
                                return receipt;
                            }
                            catch (error) {
                                console.error('[TownsUserOpClient] Failed to get user operation receipt:', error);
                                return null;
                            }
                        },
                    });
                    return polledAction?.result ?? null;
                },
            };
            return response;
        },
        setWaitTimeoutMs: (timeoutMs) => {
            waitTimeoutMs = timeoutMs;
        },
        setWaitIntervalMs: (intervalMs) => {
            waitIntervalMs = intervalMs;
        },
    };
}
